'use client';

import { OwnershipFilter } from '@components/OwnershipFilter/OwnershipFilter';
import { useCurrency } from '@contexts/CurrencyContext';
import { useMicrocopy } from '@contexts/MicrocopyContext';
import { useRouter } from '@contexts/Router/RouterContext';
import { AtomicContext } from '@contexts/coveo/AtomicContext';
import {
  AtomicFacet,
  AtomicFormatCurrency,
  AtomicNumericFacet,
  AtomicNumericRange,
  AtomicPopover,
  AtomicTimeframe,
  AtomicTimeframeFacet,
} from '@coveo/atomic-react';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { microcopySetToResourceBundles } from '@lib/coveo/microcopySetToResourceBundles';
import { useFlags } from '@lib/flags/amplitude/client';
import { FLAG_TREATMENT, Flags } from '@lib/flags/flagConstants';
import { useSession } from 'next-auth/react';
import { useContext } from 'react';
import {
  DEFAULT_LOCALE,
  formatDisplayableCurrency,
  interpolateStringWithData,
} from 'utils';
import { SaleFilter } from './SaleFilter';
import { useResults } from './hooks/useResults';

const filterStyles = `
/* filter buttons */
atomic-popover::part(popover-button) {
  border-color: var(--atomic-custom-grey-400);
  background-color: var(--atomic-custom-grey-100);
  height: 40px;
  border-radius: var(--atomic-border-radius-md);
  line-height: normal;
  --tw-ring-color: none;
}

atomic-popover::part(popover-button):hover {
  --atomic-primary-light: var(--atomic-custom-slate-500);
}

atomic-popover::part(popover-button):focus {
  background-color: var(--atomic-custom-white);
  color: var(--atomic-custom-grey-700);
  --tw-ring-color: none;
}

atomic-popover::part(popover-button):focus-visible {
  --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
  --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color);
  --tw-ring-color: var(--atomic-ring-primary) !important;
  box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000);
}

atomic-popover::part(value-label) {
  color: var(--atomic-custom-grey-600);
  letter-spacing: -0.028px;
  font-weight: 500;
}

atomic-popover:hover::part(value-label) {
  color: var(--atomic-custom-grey-700);
}

atomic-popover::part(value-count) {
  font-weight: 700;
  color: var(--atomic-custom-grey-600);
}

atomic-popover::part(arrow-icon) {
  color: var(--atomic-custom-grey-600);
  border-color: transparent;
  width: 10px;
  height: 10px;
}

/* dropdown styling */
atomic-facet,
atomic-numeric-facet,
atomic-rating-range-facet {
  padding-top: 8px;
  min-width: 208px;
}

atomic-facet::part(facet),
atomic-numeric-facet::part(facet),
atomic-rating-range-facet::part(facet){
  padding: 8px 4px;
}

/* hide count in dropdown values */
atomic-numeric-facet::part(value-count),
atomic-rating-range-facet::part(value-count) {
  display: none;
}

atomic-facet::part(values),
atomic-rating-range-facet::part(values),
atomic-numeric-facet::part(values) {
 display: flex;
 flex-direction: column;
 gap: 4px;
}

atomic-rating-range-facet::part(value-rating),
atomic-breadbox::part(breadcrumb-value) {
  --atomic-rating-icon-inactive-color: var(--atomic-custom-slate-500);
  --atomic-rating-icon-active-color: var(--atomic-custom-black);
}

atomic-facet::part(clear-button),
atomic-numeric-facet::part(clear-button),
atomic-rating-range-facet::part(clear-button) {
  display: none;
}

/* checkbox styling */
atomic-facet::part(value-checkbox),
atomic-numeric-facet::part(value-checkbox) {
  --atomic-primary-light: var(--atomic-primary);
}

atomic-facet::part(value-checkbox):hover,
atomic-numeric-facet::part(value-checkbox):hover {
  border-color: var(--atomic-custom-black);
}

atomic-facet::part(value-checkbox-label),
atomic-numeric-facet::part(value-checkbox-label) {
  --atomic-neutral-light: var(--atomic-custom-grey-200);
  --atomic-primary: var(--atomic-custom-black);
  color: var(--atomic-custom-grey-700);
  border-radius: var(--atomic-custom-spacing-1);
  transition: background-color 0.15s;
  padding: 4px 4px 4px 32px;
}

atomic-facet::part(value-checkbox-label):hover,
atomic-numeric-facet::part(value-checkbox-label):hover {
  background-color: var(--atomic-custom-grey-200);
  --atomic-primary: var(--atomic-custom-black);
}

atomic-numeric-facet::part(value-checkbox-label):active {
  background-color: var(--atomic-custom-grey-300);
}

atomic-facet::part(value-checkbox):focus,
atomic-facet::part(value-checkbox):focus-visible,
atomic-numeric-facet::part(value-checkbox):focus,
atomic-numeric-facet::part(value-checkbox):focus-visible,
atomic-facet::part(value-link):focus,
atomic-facet::part(value-link):focus-visible,
atomic-numeric-facet::part(value-link):focus,
atomic-numeric-facet::part(value-link):focus-visible,
atomic-rating-range-facet::part(value-link):focus,
atomic-rating-range-facet::part(value-link):focus-visible {
  outline: none;
  box-shadow: inset 0 0 0 2px var(--atomic-ring-primary);
  border-radius: var(--atomic-custom-spacing-1);
}

/* styling of link dropdowns (not checkbox items) */
atomic-numeric-facet::part(value-link),
atomic-rating-range-facet::part(value-link) {
  --atomic-primary: var(--atomic-custom-black);
  --atomic-neutral-light: var(--atomic-custom-grey-200);
  color: var(--atomic-custom-grey-700);
  transition: background-color 0.15s;
  padding: 4px 8px;
}

/* star icons in rating filter */
atomic-rating-range-facet::part(value-rating) {
  --atomic-rating-icon-inactive-color: var(--atomic-custom-grey-400);
}

/* hover on filters */
atomic-facet::part(value-link):hover,
atomic-numeric-facet::part(value-link):hover,
atomic-rating-range-facet::part(value-link):hover {
  background-color: var(--atomic-custom-grey-200);
  --atomic-primary: var(--atomic-custom-black);
}

/* all styling of atomic breadbox / where the selected filters appear */
atomic-breadbox::part(breadcrumb-button) {
  background-color: var(--atomic-custom-grey-200);
  border-radius: var(--atomic-custom-spacing-1);
  color: var(--atomic-custom-grey-700);
  font-weight: 500;
  --atomic-primary: var(--atomic-custom-grey-700);
}

atomic-breadbox::part(breadcrumb-label), atomic-breadbox::part(breadcrumb-value) {
  color: var(--atomic-custom-grey-700);
  transition: color 0.15s;
}

atomic-breadbox::part(breadcrumb-button):hover {
  color: var(--atomic-custom-black);
  --atomic-primary: var(--atomic-custom-black);
}

atomic-breadbox::part(breadcrumb-button), atomic-breadbox::part(clear), atomic-breadbox::part(show-more), atomic-breadbox::part(show-less)  {
  border-color: var(--atomic-custom-grey-200);
  padding: var(--atomic-custom-spacing-1) var(--atomic-custom-spacing-2);
}

atomic-breadbox::part(clear), atomic-breadbox::part(show-more), atomic-breadbox::part(show-less) {
  border: 1px solid var(--atomic-custom-slate-500);
  border-radius: var(--atomic-custom-spacing-1);
  color: var(--atomic-custom-grey-700);
  padding-left: var(--atomic-custom-spacing-4);
  padding-right: var(--atomic-custom-spacing-4);
}

atomic-breadbox::part(breadcrumb-list-container) {
  margin-bottom: var(--atomic-custom-spacing-8);
}

atomic-breadbox::part(label) {
  display: none;
}

/* override focus color of all facet headings */
atomic-category-facet::part(label-button),
atomic-rating-range-facet::part(label-button),
atomic-numeric-facet::part(label-button),
atomic-facet::part(label-button) {
  --atomic-primary-light: var(--atomic-primary);
}
`;

const getMicrocopyValues = (microcopy: MicrocopyFragment[]) => {
  const filtersMicrocopy = microcopy.find(
    (microcopyFragment) => microcopyFragment.key === 'search.filters'
  );
  const priceFilterBelowMicrocopy = microcopy.find(
    (microcopyFragment) => microcopyFragment.key === 'search.price-filter-below'
  );
  const priceFilterOverMicrocopy = microcopy.find(
    (microcopyFragment) => microcopyFragment.key === 'search.price-filter-over'
  );

  return {
    filtersMicrocopy,
    priceFilterBelowMicrocopy,
    priceFilterOverMicrocopy,
  };
};

const MIN_PRICE_FILTER_OPTION = 0.01;
const LOWER_PRICE_FILTER_OPTION = 20;
const UPPER_PRICE_FILTER_OPTION = 200;
const MAX_PRICE_FILTER = 200;
const UPPER_6000_VERSION = 600099;
const UPPER_2022_VERSION = 202299;
const UPPER_2021_VERSION = 202199;
const UPPER_2020_VERSION = 202099;
const UPPER_2019_VERSION = 201999;
const UPPER_2018_VERSION = 201899;
const UPPER_2017_VERSION = 201799;
const UPPER_BELOW_5_VERSION = 599;

export const AtomicFilters = ({ context }: { context: AtomicContext }) => {
  const { currency } = useCurrency();
  const { locale } = useRouter();
  const { searchLabelsMicrocopy, searchValuesMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
    searchValuesMicrocopy: MicrocopyFragment[];
  }>();
  const microcopyBundles = microcopySetToResourceBundles(searchValuesMicrocopy);
  const {
    filtersMicrocopy,
    priceFilterBelowMicrocopy,
    priceFilterOverMicrocopy,
  } = getMicrocopyValues(searchLabelsMicrocopy);
  const { searchEngine } = useContext(context);
  const { results: resultState } = useResults(searchEngine);
  const { flags } = useFlags();
  const isUiAndMissingFiltersEnabled =
    flags[Flags.UI_AND_MISSING_FILTERS]?.value === FLAG_TREATMENT;

  const showFilterLabel =
    resultState?.results && resultState.results.length > 0;
  const session = useSession();
  const isAuthenticated = session.status === 'authenticated';
  const showOwnershipFilter = isAuthenticated;
  // Ownership filter available to all authenticated users

  const filtersGroupLabel =
    showFilterLabel && filtersMicrocopy?.value
      ? filtersMicrocopy.value
      : 'Filters';

  return (
    <fieldset>
      <legend className="sr-only">{filtersGroupLabel}</legend>
      <style>{filterStyles}</style>
      <div className="tiny-text mb-2 text-grey-600">{filtersGroupLabel}</div>
      <div className="flex gap-2">
        {/* Having hidden facets lets us route here with the URL, but we don't want to actually show the dropdown options for publishers and release date yet */}
        <AtomicFacet
          field="publisher_name"
          label="filter-publisher-label"
          className="hidden"
        />
        <AtomicTimeframeFacet
          field="first_published_at"
          label="filter-release-date-label"
          className="hidden"
        >
          <AtomicTimeframe
            unit="day"
            amount={1}
            label={microcopyBundles?.first_published_at['1-day-ago']}
          />
          <AtomicTimeframe
            unit="day"
            amount={7}
            label={microcopyBundles?.first_published_at['1-week-ago']}
          />
          <AtomicTimeframe
            unit="day"
            amount={31}
            label={microcopyBundles?.first_published_at['1-month-ago']}
          />
          <AtomicTimeframe
            unit="day"
            amount={180}
            label={microcopyBundles?.first_published_at['6-months-ago']}
          />
          <AtomicTimeframe
            unit="day"
            amount={365}
            label={microcopyBundles?.first_published_at['1-year-ago']}
          />
        </AtomicTimeframeFacet>
        <AtomicPopover>
          <AtomicNumericFacet
            field="ec_price_filter"
            label="filter-price-label"
          >
            <AtomicNumericRange start={0} end={0} endInclusive label="Free" />
            <AtomicNumericRange
              start={MIN_PRICE_FILTER_OPTION}
              end={LOWER_PRICE_FILTER_OPTION}
              label={interpolateStringWithData(
                priceFilterBelowMicrocopy?.value ?? 'Below {{price}}',
                {
                  price: formatDisplayableCurrency(
                    LOWER_PRICE_FILTER_OPTION,
                    locale || DEFAULT_LOCALE,
                    currency
                  ),
                }
              )}
            />
            <AtomicNumericRange start={LOWER_PRICE_FILTER_OPTION} end={50} />
            <AtomicNumericRange start={50} end={100} />
            <AtomicNumericRange start={100} end={UPPER_PRICE_FILTER_OPTION} />
            <AtomicNumericRange
              start={UPPER_PRICE_FILTER_OPTION}
              end={MAX_PRICE_FILTER}
              label={interpolateStringWithData(
                priceFilterOverMicrocopy?.value ?? 'Over {{price}}',
                {
                  price: formatDisplayableCurrency(
                    UPPER_PRICE_FILTER_OPTION,
                    locale || DEFAULT_LOCALE,
                    currency
                  ),
                }
              )}
            />
            <AtomicFormatCurrency currency={currency} />
          </AtomicNumericFacet>
        </AtomicPopover>

        {isUiAndMissingFiltersEnabled ? (
          <SaleFilter context={context} isLoading={resultState?.isLoading} />
        ) : (
          <AtomicPopover>
            <AtomicFacet field="ec_sale_filters" label="filter-onsale-label" />
          </AtomicPopover>
        )}

        <AtomicPopover>
          <AtomicNumericFacet
            field="ec_rating"
            label="filter-rating-label"
            numberOfValues={5}
            sortCriteria="descending"
            displayValuesAs="link"
          >
            <AtomicNumericRange
              endInclusive
              start={1}
              end={5}
              label={`★ ${microcopyBundles?.ratings['1-star'] || '1 star'}`}
            />
            <AtomicNumericRange
              endInclusive
              start={2}
              end={5}
              label={`★ ${microcopyBundles?.ratings['2-stars'] || '2 stars'}`}
            />
            <AtomicNumericRange
              endInclusive
              start={3}
              end={5}
              label={`★ ${microcopyBundles?.ratings['3-stars'] || '3 stars'}`}
            />
            <AtomicNumericRange
              endInclusive
              start={4}
              end={5}
              label={`★ ${microcopyBundles?.ratings['4-stars'] || '4 stars'}`}
            />
            <AtomicNumericRange
              endInclusive
              start={4.75} // 4.75 is the cutoff for visually showing 5 stars
              end={5}
              label={`★ ${microcopyBundles?.ratings['5-stars'] || '5 stars'}`}
            />
          </AtomicNumericFacet>
        </AtomicPopover>

        <AtomicPopover>
          <AtomicNumericFacet
            field="min_unity_version"
            label="filter-unity-version-label"
            numberOfValues={7}
            sortCriteria="descending"
            displayValuesAs="link"
          >
            <AtomicNumericRange
              start={0}
              end={UPPER_6000_VERSION}
              label={microcopyBundles?.min_unity_version['6000x']}
            />
            <AtomicNumericRange
              start={0}
              end={UPPER_2022_VERSION}
              label={microcopyBundles?.min_unity_version['2022x']}
            />
            <AtomicNumericRange
              start={0}
              end={UPPER_2021_VERSION}
              label={microcopyBundles?.min_unity_version['2021x']}
            />
            <AtomicNumericRange
              start={0}
              end={UPPER_2020_VERSION}
              label={microcopyBundles?.min_unity_version['2020x']}
            />
            <AtomicNumericRange
              start={0}
              end={UPPER_2019_VERSION}
              label={microcopyBundles?.min_unity_version['2019x']}
            />
            <AtomicNumericRange
              start={0}
              end={UPPER_2018_VERSION}
              label={microcopyBundles?.min_unity_version['2018x']}
            />
            <AtomicNumericRange
              start={0}
              end={UPPER_2017_VERSION}
              label={microcopyBundles?.min_unity_version['2017x']}
            />
            <AtomicNumericRange
              start={0}
              end={UPPER_BELOW_5_VERSION}
              label={microcopyBundles?.min_unity_version['5x']}
            />
          </AtomicNumericFacet>
        </AtomicPopover>

        {/* Ownership filter (Atomic UI variant) */}
        {showOwnershipFilter && (
          <OwnershipFilter
            className="hidden lg:inline-flex"
            isLoading={resultState?.isLoading}
            parentSelector="[data-test='search-result-section']"
            searchEngine={searchEngine}
          />
        )}
      </div>
    </fieldset>
  );
};
